'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Check,
  Sparkles,
  ChevronDown,
  Mail,
  User,
  MessageSquare,
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const proFeatures = [
  'Booking & calendar management',
  'Client CRM & relationship tools',
  'Portfolio & discovery profile',
  'Deposits & payment processing',
  'AI business insights',
  'Follow-up & rebooking automation',
];

const clientFeatures = [
  'Search & discover professionals',
  'View portfolios & reviews',
  'Book appointments instantly',
  'Save inspiration boards',
  'Appointment reminders',
  'Message your professionals',
];

const specialties = [
  { id: 'lash', label: 'LashMatch', desc: 'Lash Artists', color: '#8b7cf8' },
  { id: 'esthe', label: 'EstheMatch', desc: 'Estheticians', color: '#10b981' },
  { id: 'brow', label: 'BrowMatch', desc: 'Brow Artists', color: '#c4b5fd' },
  { id: 'wax', label: 'WaxMatch', desc: 'Waxing', color: '#f97316' },
  { id: 'spa', label: 'SpaMatch', desc: 'Spa', color: '#a855f7' },
  { id: 'nail', label: 'NailMatch', desc: 'Nail Artists', color: '#f59e0b' },
  { id: 'makeup', label: 'MakeupMatch', desc: 'Makeup Artists', color: '#ef4444' },
  { id: 'hair', label: 'HairMatch', desc: 'Hair Professionals', color: '#06b6d4' },
  { id: 'studio', label: 'StudioMatch', desc: 'Studios & Salons', color: '#8b5cf6' },
];

function ContactSupportForm() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const { error: fnError } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact',
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        },
      });
      if (fnError) throw fnError;
      setSuccess(true);
      setForm({ name: '', email: '', message: '' });
    } catch (err: any) {
      setError(err?.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(16,185,129,0.1)' }}
        >
          <CheckCircle size={28} style={{ color: '#10b981' }} />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">Message sent!</h3>
        <p className="text-sm text-muted-foreground mb-4">
          We will get back to you within 24 hours.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="text-sm font-semibold"
          style={{ color: 'var(--primary)' }}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div
          className="flex items-start gap-2.5 p-3.5 rounded-xl"
          style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}
        >
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
          <p className="text-sm" style={{ color: '#dc2626' }}>
            {error}
          </p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
        <div className="relative">
          <User
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your full name"
            className="input-base w-full pl-9 pr-4 py-3 text-sm"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
        <div className="relative">
          <Mail
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="you@example.com"
            className="input-base w-full pl-9 pr-4 py-3 text-sm"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
        <div className="relative">
          <MessageSquare size={14} className="absolute left-3 top-3.5 text-muted-foreground" />
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="How can we help you?"
            rows={4}
            className="input-base w-full pl-9 pr-4 py-3 text-sm resize-none"
            required
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
        style={{ minHeight: '48px' }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending...
          </>
        ) : (
          <>
            <Send size={15} /> Send Message
          </>
        )}
      </button>
    </form>
  );
}

function ContactSalesForm() {
  const [form, setForm] = useState({ name: '', email: '', enquiry: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.enquiry.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const { error: fnError } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'sales',
          name: form.name.trim(),
          email: form.email.trim(),
          enquiry: form.enquiry.trim(),
        },
      });
      if (fnError) throw fnError;
      setSuccess(true);
      setForm({ name: '', email: '', enquiry: '' });
    } catch (err: any) {
      setError(err?.message || 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(16,185,129,0.1)' }}
        >
          <CheckCircle size={28} style={{ color: '#10b981' }} />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">Enquiry sent!</h3>
        <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Our sales team will reach out within 1 business day.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="text-sm font-semibold hover:underline"
          style={{ color: '#38bdf8' }}
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div
          className="flex items-start gap-2.5 p-3.5 rounded-xl"
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
          <p className="text-sm" style={{ color: '#fca5a5' }}>
            {error}
          </p>
        </div>
      )}
      <div>
        <label
          className="block text-sm font-medium mb-1.5"
          style={{ color: 'rgba(255,255,255,0.85)' }}
        >
          Name
        </label>
        <div className="relative">
          <User
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'rgba(56,189,248,0.6)' }}
          />
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your full name"
            className="w-full pl-9 pr-4 py-3 text-sm rounded-xl focus:outline-none transition-all"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(56,189,248,0.25)',
              color: '#fff',
            }}
            required
          />
        </div>
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1.5"
          style={{ color: 'rgba(255,255,255,0.85)' }}
        >
          Email
        </label>
        <div className="relative">
          <Mail
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'rgba(56,189,248,0.6)' }}
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="you@company.com"
            className="w-full pl-9 pr-4 py-3 text-sm rounded-xl focus:outline-none transition-all"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(56,189,248,0.25)',
              color: '#fff',
            }}
            required
          />
        </div>
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1.5"
          style={{ color: 'rgba(255,255,255,0.85)' }}
        >
          Enquiry
        </label>
        <div className="relative">
          <MessageSquare
            size={14}
            className="absolute left-3 top-3.5"
            style={{ color: 'rgba(56,189,248,0.6)' }}
          />
          <textarea
            value={form.enquiry}
            onChange={(e) => setForm((f) => ({ ...f, enquiry: e.target.value }))}
            placeholder="Tell us about your business needs, team size, and what you are looking for..."
            rows={4}
            className="w-full pl-9 pr-4 py-3 text-sm rounded-xl resize-none focus:outline-none transition-all"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(56,189,248,0.25)',
              color: '#fff',
            }}
            required
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
        style={{ minHeight: '48px' }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending...
          </>
        ) : (
          <>
            <Send size={15} /> Send Enquiry
          </>
        )}
      </button>
    </form>
  );
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* ── NAV ── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'border-b border-sky-400/25 dark:border-sky-500/20 backdrop-blur-xl shadow-[0_4px_30px_rgba(56,189,248,0.12)]'
            : ''
        }`}
        style={{
          background: scrolled
            ? 'var(--nav-scrolled-bg, rgba(244,249,253,0.92))'
            : 'transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src="/assets/images/file_00000000049881f4ba7bdc2dc261021b-1786785034842.png"
                alt="AVEN Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span
              className="font-black text-xl tracking-tight"
              style={{ color: 'var(--foreground)' }}
            >
              AvenLink
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#discover"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Discover
            </a>
            <a
              href="#professionals"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              For Professionals
            </a>
            <a
              href="#pricing"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </a>
            <a
              href="#contact"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-2 hidden sm:block"
            >
              Sign in
            </Link>
            <Link
              href="/sign-up-login-screen?tab=signup"
              className="btn-primary text-sm px-4 py-2.5 flex items-center gap-1.5"
            >
              Get started <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-24 pb-20 sm:pt-32 sm:pb-28">
        {/* Electric Winter Frosty Blue Glow - Top Right */}
        <div
          className="absolute top-0 right-0 w-[750px] h-[750px] rounded-full pointer-events-none opacity-50 dark:opacity-60"
          style={{
            background:
              'radial-gradient(circle, rgba(56,189,248,0.28) 0%, rgba(0,229,255,0.15) 35%, transparent 70%)',
            transform: 'translate(25%, -25%)',
          }}
        />
        {/* Shining Antique Gold Radiant Glow - Bottom Left */}
        <div
          className="absolute bottom-0 left-0 w-[550px] h-[550px] rounded-full pointer-events-none opacity-40 dark:opacity-45"
          style={{
            background:
              'radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(243,216,130,0.12) 40%, transparent 70%)',
            transform: 'translate(-25%, 25%)',
          }}
        />
        {/* Ambient Top Center Frost Ribbon */}
        <div
          className="absolute top-10 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full pointer-events-none opacity-30 dark:opacity-35"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(0,229,255,0.14) 0%, rgba(212,175,55,0.08) 50%, transparent 75%)',
          }}
        />

        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs sm:text-sm font-semibold shadow-[0_0_20px_rgba(56,189,248,0.22)] backdrop-blur-md"
            style={{
              background:
                'linear-gradient(135deg, rgba(56,189,248,0.12) 0%, rgba(212,175,55,0.12) 100%)',
              border: '1px solid rgba(212,175,55,0.38)',
              color: 'var(--foreground)',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-[#00E5FF] shadow-[0_0_10px_#00E5FF] animate-pulse" />
            <span className="bg-gradient-to-r from-[#0284c7] via-[#0ea5e9] to-[#d4af37] dark:from-[#38bdf8] dark:via-[#a5f3fc] dark:to-[#f3d882] bg-clip-text text-transparent font-bold tracking-wide">
              Beauty discovery &amp; booking platform
            </span>
            <Sparkles size={13} className="text-[#D4AF37] animate-pulse" />
          </div>

          <h1
            className="font-black leading-[1.06] mb-6 text-foreground"
            style={{ fontSize: 'clamp(2.6rem, 7vw, 5.5rem)', letterSpacing: '-0.02em' }}
          >
            Discover your
            <br />
            <span className="text-gradient-lavender">beauty people.</span>
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto">
            Discover beauty professionals you&apos;ll love. Build your beauty business and connect with new clients.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              href="/sign-up-login-screen?role=client&tab=signup"
              className="w-full sm:w-auto px-8 py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-0.5 border border-sky-400/25 dark:border-sky-400/35"
              style={{
                background: 'var(--foreground)',
                color: 'var(--background)',
                boxShadow: '0 4px 20px rgba(56,189,248,0.22)',
              }}
            >
              <span>I&apos;m a Client</span>
              <ArrowRight size={16} />
            </Link>
            <Link
              href="/sign-up-login-screen?tab=signup"
              className="btn-primary w-full sm:w-auto px-8 py-4 text-base font-semibold flex items-center justify-center gap-2"
            >
              <Sparkles size={16} />
              <span>I&apos;m a Beauty Professional</span>
            </Link>
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <a
            href="#discover"
            className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="text-xs font-medium">Explore</span>
            <ChevronDown size={16} className="animate-bounce" />
          </a>
        </div>
      </section>

      {/* ── DISCOVER SPECIALTIES ── */}
      <section id="discover" className="py-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-3">
              Every beauty specialty, one platform
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              AvenLink connects clients with the best beauty professionals across every specialty.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {specialties?.map((s) => (
              <Link
                key={s?.id}
                href={`/sign-up-login-screen?role=${s?.id}`}
                className="card-base p-4 text-center hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 group"
              >
                <div className="text-xs font-bold mb-0.5" style={{ color: s?.color }}>
                  {s?.label}
                </div>
                <div className="text-xs text-muted-foreground">{s?.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── TWO PATHS ── */}
      <section id="professionals" className="py-20 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client path */}
            <div
              className="card-base rounded-2xl p-8 sm:p-10 relative overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_rgba(56,189,248,0.15)]"
              style={{
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="absolute top-0 right-0 w-52 h-52 rounded-full pointer-events-none opacity-25"
                style={{
                  background:
                    'radial-gradient(circle, rgba(56,189,248,0.25) 0%, rgba(212,175,55,0.15) 50%, transparent 70%)',
                  transform: 'translate(25%, -25%)',
                }}
              />
              <div className="relative z-10">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-6"
                  style={{
                    background: 'rgba(56,189,248,0.12)',
                    color: '#0284c7',
                    border: '1px solid rgba(56,189,248,0.25)',
                  }}
                >
                  For Clients
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-foreground mb-3">
                  Find beauty professionals.
                  <br />
                  Discover inspiration.
                  <br />
                  Book.
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Search verified professionals near you, view their portfolios, read real reviews,
                  and book instantly.
                </p>
                <ul className="space-y-2.5 mb-8">
                  {clientFeatures?.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                      <Check size={14} className="text-[#0284c7] dark:text-[#38bdf8] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-3">
                  <Link
                    href="/sign-up-login-screen?role=client&tab=signup"
                    className="btn-primary px-6 py-3 text-sm font-semibold flex items-center gap-2"
                  >
                    Find Artists <ArrowRight size={14} />
                  </Link>
                  <span className="text-xs text-muted-foreground">Free to join</span>
                </div>
              </div>
            </div>

            {/* Professional path */}
            <div
              className="rounded-2xl p-8 sm:p-10 relative overflow-hidden transition-all duration-300 hover:shadow-[0_12px_45px_rgba(56,189,248,0.25)]"
              style={{
                background: 'linear-gradient(160deg, #070C16 0%, #0E182D 55%, #152542 100%)',
                border: '1px solid rgba(56,189,248,0.35)',
                boxShadow: '0 8px 32px rgba(7,12,22,0.6), inset 0 0 24px rgba(56,189,248,0.08)',
              }}
            >
              <div
                className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none opacity-30"
                style={{
                  background:
                    'radial-gradient(circle, rgba(56,189,248,0.45) 0%, rgba(212,175,55,0.2) 45%, transparent 70%)',
                  transform: 'translate(25%, -25%)',
                }}
              />
              <div className="relative z-10">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-6"
                  style={{
                    background: 'rgba(56,189,248,0.18)',
                    color: '#38bdf8',
                    border: '1px solid rgba(56,189,248,0.4)',
                  }}
                >
                  For Professionals
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">
                  Build your profile.
                  <br />
                  Get discovered.
                  <br />
                  Grow your business.
                </h3>
                <p className="mb-6 leading-relaxed" style={{ color: 'rgba(230,243,253,0.7)' }}>
                  Everything you need to attract clients, manage bookings, and grow your beauty
                  business.
                </p>
                <ul className="space-y-2.5 mb-8">
                  {proFeatures?.map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2.5 text-sm"
                      style={{ color: 'rgba(230,243,253,0.85)' }}
                    >
                      <Check size={14} className="text-[#38bdf8] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    href="/sign-up-login-screen?tab=signup"
                    className="btn-primary px-6 py-3 text-sm font-semibold flex items-center gap-2"
                  >
                    <Sparkles size={14} />
                    Start Free — 30 Days Pro
                  </Link>
                  <span className="text-xs" style={{ color: 'rgba(230,243,253,0.5)' }}>
                    No credit card required
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-3">
              Simple, transparent pricing
            </h2>
            <p className="text-muted-foreground text-lg">
              Start with 30 days of Pro — free. No credit card required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
            {/* Freemium */}
            <div className="card-base p-7">
              <div className="text-xs font-bold text-muted-foreground mb-4 uppercase tracking-wider">
                Freemium
              </div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-black text-foreground">$0</span>
                <span className="text-sm text-muted-foreground mb-1.5">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">Get started for free.</p>
              <Link
                href="/sign-up-login-screen?tab=signup"
                className="btn-secondary w-full py-3 text-sm font-semibold text-center block mb-6"
              >
                Get Started Free
              </Link>
              <ul className="space-y-2">
                {[
                  'Basic profile',
                  'Limited discovery',
                  'Up to 10 appointments/month',
                  'Basic booking page',
                  'Service menu',
                ]?.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={13} className="text-[#536E8A] dark:text-[#8BA8C7] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Basic */}
            <div className="card-base p-7">
              <div className="text-xs font-bold text-muted-foreground mb-4 uppercase tracking-wider">
                Basic
              </div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-black text-foreground">$19</span>
                <span className="text-sm text-muted-foreground mb-1.5">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                For solo professionals getting started.
              </p>
              <Link
                href="/sign-up-login-screen?tab=signup"
                className="btn-secondary w-full py-3 text-sm font-semibold text-center block mb-6"
              >
                Get Basic
              </Link>
              <ul className="space-y-2">
                {[
                  'Up to 30 appointments/month',
                  'Up to 50 clients',
                  'Custom booking page',
                  'Email reminders',
                  'Calendar & deposits',
                ]?.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={13} className="text-[#536E8A] dark:text-[#8BA8C7] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro — featured */}
            <div
              className="rounded-2xl p-7 relative overflow-hidden"
              style={{
                background: 'linear-gradient(160deg, #070C16 0%, #0E1A33 100%)',
                border: '2px solid rgba(212,175,55,0.7)',
                boxShadow:
                  '0 8px 40px rgba(56,189,248,0.25), 0 0 24px rgba(212,175,55,0.25)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 py-1.5 text-center text-xs font-bold tracking-wider uppercase"
                style={{
                  background: 'linear-gradient(135deg, #F3D882 0%, #D4AF37 50%, #B8860B 100%)',
                  color: '#070C16',
                }}
              >
                Most Popular
              </div>
              <div className="mt-5">
                <div
                  className="text-xs font-bold mb-4 uppercase tracking-wider"
                  style={{ color: '#F3D882' }}
                >
                  Pro
                </div>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-black text-white">$39</span>
                  <span className="text-sm mb-1.5" style={{ color: 'rgba(230,243,253,0.6)' }}>
                    /mo
                  </span>
                </div>
                <p className="text-sm mb-6" style={{ color: 'rgba(230,243,253,0.7)' }}>
                  For growing professionals.
                </p>
                <Link
                  href="/sign-up-login-screen?tab=signup"
                  className="btn-primary w-full py-3 text-sm font-semibold text-center block mb-6"
                >
                  Start 30-Day Free Trial
                </Link>
                <ul className="space-y-2">
                  {[
                    'Unlimited appointments',
                    'Unlimited clients',
                    'Custom booking page',
                    'SMS & email reminders',
                    'Revenue analytics',
                    'AI Coach',
                    'Lash/brow mapping tools',
                    'Portfolio gallery',
                  ]?.map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: 'rgba(230,243,253,0.85)' }}
                    >
                      <Check size={13} className="text-[#38bdf8] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Enterprise — Contact Sales */}
            <div
              className="rounded-2xl p-7 relative overflow-hidden"
              style={{
                background: 'linear-gradient(160deg, #060B14 0%, #0A1326 100%)',
                border: '1px solid rgba(56,189,248,0.35)',
                boxShadow: '0 8px 30px rgba(0,229,255,0.08)',
              }}
            >
              <div
                className="text-xs font-bold mb-4 uppercase tracking-wider"
                style={{ color: '#38BDF8' }}
              >
                Enterprise
              </div>
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-black text-white">Custom</span>
              </div>
              <p className="text-sm mb-6" style={{ color: 'rgba(230,243,253,0.65)' }}>
                For studios and multi-location businesses.
              </p>

              <ContactSalesForm />
            </div>
          </div>

          <div
            className="rounded-xl p-5 text-center"
            style={{
              background: 'rgba(56,189,248,0.06)',
              border: '1px solid rgba(56,189,248,0.2)',
            }}
          >
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Clients:</span> Free to join,
              discover, and book. No subscription required.
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="rounded-2xl p-10 sm:p-14 relative overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, #070C16 0%, #0E182E 55%, #142442 100%)',
              border: '1px solid rgba(212,175,55,0.4)',
              boxShadow: '0 12px 48px rgba(7,12,22,0.8), 0 0 32px rgba(56,189,248,0.18)',
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 0%, rgba(56,189,248,0.28) 0%, rgba(212,175,55,0.15) 45%, transparent 70%)',
              }}
            />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                Start your 30-day Pro trial
              </h2>
              <p className="mb-8 text-lg" style={{ color: 'rgba(230,243,253,0.7)' }}>
                No credit card required. Full Pro access for 30 days.
                <br />
                Then choose the plan that fits your business.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href="/sign-up-login-screen?tab=signup"
                  className="btn-primary px-8 py-4 text-base font-semibold flex items-center gap-2"
                >
                  <Sparkles size={16} />
                  Get Started Free
                </Link>
                <Link
                  href="/sign-up-login-screen?role=client&tab=signup"
                  className="px-8 py-4 rounded-xl text-base font-semibold flex items-center gap-2 transition-all hover:bg-sky-500/10"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(230,243,253,0.9)',
                    border: '1px solid rgba(56,189,248,0.3)',
                    boxShadow: '0 0 16px rgba(56,189,248,0.1)',
                  }}
                >
                  Find an Artist <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT / SUPPORT ── */}
      <section id="contact" className="py-20 px-5 sm:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-3">Get in touch</h2>
            <p className="text-muted-foreground text-lg">
              Have a question or need support? We are here to help.
            </p>
          </div>
          <div className="card-base p-8">
            <ContactSupportForm />
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-5 sm:px-8 border-t border-border/60">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/assets/images/file_00000000049881f4ba7bdc2dc261021b-1786785034842.png"
              alt="AVEN Logo"
              width={28}
              height={28}
              className="w-7 h-7 object-contain flex-shrink-0"
            />
            <span className="font-black text-lg tracking-tight text-foreground">AvenLink</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">
              Privacy
            </a>
            <a href="#" className="hover:text-foreground transition-colors">
              Terms
            </a>
            <a href="#contact" className="hover:text-foreground transition-colors">
              Support
            </a>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 AvenLink. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
